import {
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormInvalidFeedback,
  BModal,
} from "bootstrap-vue";

const createEmptyAction = () => ({
  id: null,
  title: "",
  level_id: null,
  type: "free_shipping",
  type_value: 0,
  start_date: "",
  end_date: "",
  index: null,
});

const normalizeAction = (source) => {
  const value = source || {};
  const type = value.type || "free_shipping";

  return {
    ...createEmptyAction(),
    ...value,
    level_id:
      value.level_id ||
      (value.level && value.level.id ? value.level.id : null),
    type,
    type_value:
      type === "wallet_charge" &&
        value.type_value !== null &&
        typeof value.type_value !== "undefined"
        ? value.type_value
        : type === "wallet_charge"
          ? ""
          : 0,
  };
};

export default {
  name: "ActionForm",

  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormInvalidFeedback,
  },

  props: {
    mainData: {
      type: Object,
      default: () => createEmptyAction(),
    },
    mode: {
      type: String,
      default: "create",
    },
    url: {
      type: String,
      required: true,
    },
    levels: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      action: normalizeAction(this.mainData),
      disabled: false,
      errors: {},
    };
  },

  computed: {
    levelOptions() {
      return this.levels.map((level) => ({
        value: level.id,
        text:
          level.title ||
          level.name ||
          level.label ||
          level.display_name ||
          `سطح ${level.id}`,
      }));
    },
  },

  watch: {
    mainData: {
      immediate: true,
      deep: true,
      handler(value) {
        this.action = normalizeAction(value);
        this.errors = {};
      },
    },
  },

  methods: {
    fieldState(field) {
      return this.errors[field] ? false : null;
    },

    clearFieldError(field) {
      if (!this.errors[field]) {
        return;
      }

      this.$delete(this.errors, field);
    },

    handleTypeChange(type) {
      this.clearFieldError("type");
      this.clearFieldError("type_value");

      if (type === "free_shipping") {
        this.action.type_value = 0;
        return;
      }

      if (type === "wallet_charge" && Number(this.action.type_value) === 0) {
        this.action.type_value = "";
      }
    },

    validateForm() {
      const errors = {};
      const title = String(this.action.title || "").trim();

      if (!title) {
        errors.title = "عنوان عملیات را وارد کنید.";
      } else if (title.length > 255) {
        errors.title = "عنوان عملیات نباید بیشتر از ۲۵۵ کاراکتر باشد.";
      }

      if (
        this.action.level_id === null ||
        this.action.level_id === "" ||
        typeof this.action.level_id === "undefined"
      ) {
        errors.level_id = "یک سطح را انتخاب کنید.";
      }

      if (!["free_shipping", "wallet_charge"].includes(this.action.type)) {
        errors.type = "نوع عملیات معتبر نیست.";
      }

      if (this.action.type === "wallet_charge") {
        const value = this.action.type_value;

        if (value === "" || value === null || typeof value === "undefined") {
          errors.type_value = "مبلغ شارژ کیف پول را وارد کنید.";
        } else if (Number.isNaN(Number(value))) {
          errors.type_value = "مبلغ شارژ کیف پول باید عدد باشد.";
        } else if (Number(value) < 0) {
          errors.type_value = "مبلغ شارژ کیف پول نمی‌تواند منفی باشد.";
        }
      }

      if (!this.action.start_date) {
        errors.start_date = "تاریخ شروع را انتخاب کنید.";
      }

      if (!this.action.end_date) {
        errors.end_date = "تاریخ پایان را انتخاب کنید.";
      }

      if (this.action.start_date && this.action.end_date) {
        const start = this.parseDate(this.action.start_date);
        const end = this.parseDate(this.action.end_date);

        if (!start || !start.isValid()) {
          errors.start_date = "تاریخ شروع معتبر نیست.";
        }

        if (!end || !end.isValid()) {
          errors.end_date = "تاریخ پایان معتبر نیست.";
        }

        if (
          start &&
          start.isValid() &&
          end &&
          end.isValid() &&
          end.isBefore(start)
        ) {
          errors.end_date = "تاریخ پایان باید برابر یا بعد از تاریخ شروع باشد.";
        }
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    parseDate(value) {
      if (!value || !window.moment) {
        return null;
      }

      const formats = [
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY.MM.DD HH:mm",
        window.moment.ISO_8601,
      ];

      let date = window.moment(value, formats, true);

      if (!date.isValid()) {
        date = window.moment(value);
      }

      return date;
    },

    normalizeDate(value) {
      const date = this.parseDate(value);
      return date && date.isValid()
        ? date.format("YYYY-MM-DD HH:mm")
        : value || "";
    },

    buildFormData() {
      const fd = new FormData();

      fd.append("title", String(this.action.title || "").trim());
      fd.append("level_id", this.action.level_id);
      fd.append("type", this.action.type);

      fd.append(
        "type_value",
        this.action.type === "wallet_charge"
          ? Number(this.action.type_value)
          : 0
      );

      fd.append("start_date", this.normalizeDate(this.action.start_date));
      fd.append("end_date", this.normalizeDate(this.action.end_date));

      if (this.mode === "edit") {
        fd.append("_method", "PUT");
      }

      return fd;
    },

    extractSavedAction(response) {
      const body = response && response.data ? response.data : {};

      if (body.data && body.data.action) {
        return body.data.action;
      }

      if (body.action) {
        return body.action;
      }

      if (body.data && typeof body.data === "object") {
        return body.data;
      }

      return null;
    },

    attachLevel(savedAction) {
      const result = {
        ...this.action,
        ...(savedAction || {}),
        level_id:
          (savedAction && savedAction.level_id) || this.action.level_id,
      };

      if (!result.level) {
        result.level = this.levels.find(
          (level) => level.id == result.level_id
        );
      }

      return result;
    },

    applyServerErrors(error) {
      const response = error && error.response ? error.response : null;
      const serverErrors =
        response && response.data && response.data.errors
          ? response.data.errors
          : null;

      if (!serverErrors || typeof serverErrors !== "object") {
        return;
      }

      const mappedErrors = {};

      Object.keys(serverErrors).forEach((field) => {
        const messages = serverErrors[field];
        mappedErrors[field] = Array.isArray(messages)
          ? messages[0]
          : String(messages);
      });

      this.errors = {
        ...this.errors,
        ...mappedErrors,
      };
    },

    async submit(event) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      if (this.disabled || !this.validateForm()) {
        return;
      }

      this.disabled = true;

      try {
        const response = await this.$axios.post(this.url, this.buildFormData());
        const savedAction = this.attachLevel(
          this.extractSavedAction(response)
        );
        const message =
          response && response.data && response.data.message
            ? response.data.message
            : this.mode === "edit"
              ? "عملیات با موفقیت ویرایش شد."
              : "عملیات با موفقیت ثبت شد.";

        this.$root.notify(message, "success");
        this.$emit("saved", savedAction, this.mode, this.action.index);
        this.$root.$emit("bv::hide::modal", "action-modal");
      } catch (error) {
        this.applyServerErrors(error);
        this.$root.notify(error);
      } finally {
        this.disabled = false;
      }
    },

    onHidden() {
      this.errors = {};
      this.disabled = false;
    },
  },
};
